import cn from 'classnames';
import PropTypes from 'prop-types';

import { IMAGE, MSA_CC_IMAGES_URL, UPLOADED_BY_REVIEWER_ALT_TEXT, VIDEO } from 'constants/appConstants';

import ImageLazyLoader from 'components/common/ImageLazyLoader';
import marketplace from 'cfg/marketplace.json';
import { MSA_IMAGE_ID_FROM_URL } from 'common/regex';
import { constructMSAImageUrl } from 'helpers';
import { userAgentStringToPlatform } from 'helpers/UserAgentUtils';
import { logError } from 'middleware/logger';
import css from 'styles/components/reviews/reviewGalleryMediaGridElement.scss';

function makeImageElement(src, { testId }) {
  const divProps = {
    'alt': UPLOADED_BY_REVIEWER_ALT_TEXT,
    'className': css.image,
    'data-test-id': testId('reviewerMedia')
  };

  const imageId = src.match(MSA_IMAGE_ID_FROM_URL)[0];
  const imgSrc = constructMSAImageUrl(imageId, { width: 220 }, MSA_CC_IMAGES_URL);

  const imgProps = { ...divProps, src: imgSrc };

  const divBgImg = <div {...divProps} style={{ backgroundImage: `url(${imgSrc})` }} />;
  const placeholder = <div className={css.placeholder} />;

  const platform = userAgentStringToPlatform();
  const preventOpacityTransition = platform === 'ipad';

  const imageLoader = <ImageLazyLoader
    divBgImg={divBgImg}
    preventOpacityTransition={preventOpacityTransition}
    imgProps={imgProps}
    placeholder={placeholder} />;

  return imageLoader;
}

function makeMediaElement({ mediaItem, showVideoThumbnails }, context) {
  const { mediaType, mediaUrl, msaMediaUrl } = mediaItem;
  if (mediaType === IMAGE) {
    return makeImageElement(msaMediaUrl || mediaUrl, context);
  } else if (mediaType === VIDEO) {
    return showVideoThumbnails ?
      <>
        <video className={css.image} src={msaMediaUrl} />
        <span className={css.playVideoImgOverlay}>
          <span className={css.playVideoButton} />
        </span>
      </> :
      <span className={css.videoPlaceholder}>
        <span className={css.playVideoButton} />
      </span>;
  }
  logError(`only IMAGE and VIDEO media are currently supported (attempted to render item with mediaType ${mediaType})`);
  return null;
}

function ReviewGalleryMediaGridElement(props, context) {
  const { compact, onOpenMediaReview, mediaIndex, reviewId } = props;
  const buttonOnClick = onOpenMediaReview.bind(this, reviewId, mediaIndex);

  return (
    <span className={cn(css.carouselChild, { [css.compact]: compact })}>
      <button type="button" className={css.imageButton} onClick={buttonOnClick}>
        {makeMediaElement(props, context)}
      </button>
    </span>
  );
}

ReviewGalleryMediaGridElement.defaultProps = {
  compact: false,
  imageBaseUrl: marketplace.desktopBaseUrl
};

ReviewGalleryMediaGridElement.propTypes = {
  compact: PropTypes.bool.isRequired,
  imageBaseUrl: PropTypes.string.isRequired,
  onOpenMediaReview: PropTypes.func.isRequired,
  mediaIndex: PropTypes.number.isRequired,
  mediaItem: PropTypes.shape({
    mediaType: PropTypes.string.isRequired,
    msaMediaUrl: PropTypes.string,
    mediaUrl: PropTypes.string.isRequired
  }).isRequired,
  reviewId: PropTypes.string.isRequired
};

ReviewGalleryMediaGridElement.contextTypes = {
  testId: PropTypes.func.isRequired
};

export default ReviewGalleryMediaGridElement;
